import { useCallback, useEffect, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import axiosInstance from "config/axiosInstance";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import useWhatsAppEvents from "../hooks/useWhatsAppEvents";
import useWhatsAppRecipients from "../hooks/useWhatsAppRecipients";
import WaOutlineButton from "./WaOutlineButton";
import NotificationRecipientsSummary, {
  formatDisplayPhone,
} from "./NotificationRecipientsSummary";

const STATUS_CONFIG = {
  connected: { label: "Conectado", color: "success" },
  disconnected: { label: "Desconectado", color: "default" },
  paused: { label: "Pausado", color: "warning" },
  idle: { label: "Sin sesión", color: "default" },
  awaiting_qr: { label: "Esperando QR", color: "info" },
  initializing: { label: "Iniciando", color: "info" },
  pending_validation: { label: "Pendiente validación", color: "warning" },
  validating: { label: "Validando número", color: "warning" },
  reconnecting: { label: "Reconectando", color: "info" },
  validation_failed: { label: "Validación fallida", color: "error" },
  error: { label: "Error", color: "error" },
};

const QR_POLL_MS = 5000;
const QR_POLL_AFTER_CREATE_MS = 2000;
const QR_POLL_AFTER_CREATE_MAX_MS = 90000;
const DEV_LOG = process.env.NODE_ENV !== "production";

function getStatusMeta(status) {
  return STATUS_CONFIG[status] || { label: status || "Desconocido", color: "default" };
}

function normalizeQrSrc(qr) {
  if (!qr || typeof qr !== "string") return null;
  const trimmed = qr.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:image")) return trimmed;
  return `data:image/png;base64,${trimmed}`;
}

function mergeSessionStatus(prev, incoming) {
  if (!incoming) return prev;
  const next = { ...(prev || {}), ...incoming };
  const incomingQr = normalizeQrSrc(incoming.qr);
  if (incomingQr) {
    next.qr = incomingQr;
  } else if (prev?.qr && !incoming.connected && incoming.qrAvailable !== false && !incoming.staleSession) {
    next.qr = prev.qr;
  } else if (incoming.connected || incoming.status === "connected" || incoming.staleSession) {
    next.qr = null;
  }
  return next;
}

function isStaleQrSession(status) {
  if (!status) return false;
  if (status.staleSession || status.canCreateSession) return true;
  const err = String(status.lastError || "");
  if (/expirado|genere uno nuevo/i.test(err)) return true;
  if (status.qr || status.qrAvailable) return false;
  if (
    status.status === "awaiting_qr" ||
    status.status === "initializing" ||
    status.status === "reconnecting"
  ) {
    return true;
  }
  return false;
}

function isActiveQrWait(status) {
  if (!status || status.connected || isStaleQrSession(status)) return false;
  if (status.status === "pending_validation" || status.status === "validating") return true;
  if (status.status === "reconnecting") return true;
  if (status.status === "initializing") return true;
  if (status.status === "awaiting_qr" && (status.qr || status.qrAvailable)) {
    return true;
  }
  return false;
}

function formatWaApiError(err, fallback) {
  const data = err?.response?.data || {};
  if (data.code === "WA_AUTH") {
    return "API key incorrecta entre backend y microservicio WhatsApp (WHATSAPP_API_KEY / API_KEY).";
  }
  if (data.code === "WA_OFFLINE" || data.code === "WA_UNREACHABLE" || data.code === "WA_NOT_CONFIGURED") {
    return data.message || fallback;
  }
  if (data.code === "WA_UPSTREAM_ERROR") {
    return (
      data.message ||
      "El microservicio WhatsApp falló. Compruebe https://wa.cuentamelon.pe/health en el servidor."
    );
  }
  return data.message || fallback;
}

function getStatusWarning(data) {
  if (!data) return "";
  if (data.serviceWarning) return String(data.serviceWarning);
  if (data.degraded) {
    return "Servicio WhatsApp degradado — compruebe https://wa.cuentamelon.pe/health";
  }
  return "";
}

function needsSessionCleanup(status) {
  if (!status || status.connected) return false;
  if (isStaleQrSession(status)) return true;
  if (status.lastError) return true;
  return ["awaiting_qr", "initializing", "reconnecting", "error", "validation_failed"].includes(
    status.status
  );
}

export default function SessionPanel({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [recipientsRefreshKey, setRecipientsRefreshKey] = useState(0);
  const pollRef = useRef(null);
  const connectInFlightRef = useRef(false);

  const { recipients: resolvedRecipients } = useWhatsAppRecipients({
    enabled: true,
    refreshKey: recipientsRefreshKey,
  });

  const displayRecipients =
    resolvedRecipients?.count > 0
      ? resolvedRecipients
      : status?.notificationRecipients?.count > 0
        ? status.notificationRecipients
        : resolvedRecipients || status?.notificationRecipients;

  const isSessionBusy =
    actionLoading ||
    isActiveQrWait(status);

  const applyStatus = useCallback(
    (data, { source = "status" } = {}) => {
      setStatus((prev) => {
        const merged = mergeSessionStatus(prev, data);
        if (DEV_LOG && prev?.status !== merged?.status) {
          console.log("[SessionPanel] state transition", {
            source,
            from: prev?.status,
            to: merged?.status,
            qrAvailable: merged?.qrAvailable,
            hasQr: Boolean(merged?.qr),
          });
        }
        if (prev?.status !== merged?.status) {
          onStatusChange?.(merged);
        }
        return merged;
      });
    },
    [onStatusChange]
  );

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/whatsapp/session/status");
      if (res.data?.success !== false) {
        applyStatus(res.data, { source: "fetchStatus" });
        setRecipientsRefreshKey((k) => k + 1);
        setError(getStatusWarning(res.data));
      }
    } catch (err) {
      setError(formatWaApiError(err, "No se pudo obtener el estado"));
    } finally {
      setLoading(false);
    }
  }, [applyStatus]);

  const fetchQr = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/whatsapp/session/qr");
      if (res.data) {
        const qrSrc = normalizeQrSrc(res.data.qr);
        if (DEV_LOG) {
          console.log("[SessionPanel] QR received frontend", {
            status: res.data.status,
            qrAvailable: res.data.qrAvailable,
            qrLength: qrSrc ? qrSrc.length : 0,
          });
        }
        applyStatus(
          {
            ...res.data,
            qr: qrSrc,
          },
          { source: "fetchQr" }
        );
        return Boolean(qrSrc) || res.data.status === "connected";
      }
    } catch (err) {
      if (DEV_LOG) {
        console.warn("[SessionPanel] fetchQr pending", err.response?.status || err.message);
      }
    }
    return false;
  }, [applyStatus]);

  /** Tras create, el QR suele llegar async — poll cada 2s hasta verlo o conectar. */
  const pollQrAfterCreate = useCallback(async () => {
    const deadline = Date.now() + QR_POLL_AFTER_CREATE_MAX_MS;
    while (Date.now() < deadline) {
      const done = await fetchQr();
      if (done) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, QR_POLL_AFTER_CREATE_MS));
    }
  }, [fetchQr]);

  const handleConnect = async () => {
    if (connectInFlightRef.current || isSessionBusy) {
      return;
    }
    if (isActiveQrWait(status) && !isStaleQrSession(status)) {
      setError("La sesión ya se está iniciando. Espera el QR o usa Actualizar.");
      return;
    }

    connectInFlightRef.current = true;
    setActionLoading(true);
    setError("");
    try {
      const res = await axiosInstance.post("/whatsapp/session/create", null, {
        timeout: 130000,
      });
      const payload = {
        ...res.data,
        qr: normalizeQrSrc(res.data?.qr),
      };
      applyStatus(payload, { source: "create" });
      if (payload.qr) {
        if (DEV_LOG) {
          console.log("[SessionPanel] QR from create response", {
            qrLength: payload.qr.length,
          });
        }
      } else if (!payload.connected && payload.status !== "connected") {
        await pollQrAfterCreate();
      }
    } catch (err) {
      setError(formatWaApiError(err, "Error al conectar"));
      fetchStatus();
    } finally {
      connectInFlightRef.current = false;
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    setError("");
    try {
      const res = await axiosInstance.post("/whatsapp/session/pause");
      applyStatus(
        {
          ...(status || {}),
          status: "paused",
          connected: false,
          paused: true,
          qr: null,
          qrAvailable: false,
        },
        { source: "pause" }
      );
      onStatusChange?.({ status: "paused", connected: false });
    } catch (err) {
      setError(err.response?.data?.message || "Error al pausar");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    if (
      !window.confirm(
        "Cerrar sesión eliminará la vinculación WhatsApp y la carpeta de sesión. ¿Continuar?"
      )
    ) {
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await axiosInstance.post("/whatsapp/session/logout");
      applyStatus(
        {
          status: "idle",
          connected: false,
          paused: false,
          qr: null,
          qrAvailable: false,
          connectedPhone: null,
          whatsappName: null,
          verified: false,
          authPreserved: false,
          lastError: null,
          staleSession: false,
        },
        { source: "logout" }
      );
      onStatusChange?.({ status: "idle", connected: false });
    } catch (err) {
      setError(err.response?.data?.message || "Error al cerrar sesión");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetSession = async () => {
    if (
      !window.confirm(
        "¿Limpiar la sesión atascada? Se borrará el QR expirado y podrá generar uno nuevo."
      )
    ) {
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await axiosInstance.post("/whatsapp/session/reset", { keepAuth: false });
      applyStatus(
        {
          status: "idle",
          connected: false,
          paused: false,
          qr: null,
          qrAvailable: false,
          lastError: null,
          staleSession: false,
          authPreserved: false,
        },
        { source: "reset" }
      );
      onStatusChange?.({ status: "idle", connected: false });
    } catch (err) {
      setError(err.response?.data?.message || "Error al resetear sesión");
      fetchStatus();
    } finally {
      setActionLoading(false);
    }
  };

  useWhatsAppEvents({
    enabled: true,
    onEvent: (event) => {
      if (
        [
          "session.connected",
          "session.disconnected",
          "session.qr",
          "session.invalid_number",
          "validation.failed",
          "session.reconnecting",
        ].includes(event.type)
      ) {
        if (DEV_LOG) {
          console.log("[SessionPanel] SSE event", event.type, event.payload || {});
        }
        if (event.type === "session.invalid_number") {
          setError(
            event.payload?.message ||
              "El número conectado no coincide con el número principal registrado."
          );
        }
        if (event.type === "session.qr") {
          fetchQr();
        } else {
          fetchStatus();
        }
      }
    },
  });

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const currentStatus = status?.status;
    const needsQr =
      !isStaleQrSession(status) &&
      (currentStatus === "awaiting_qr" ||
        currentStatus === "initializing" ||
        currentStatus === "pending_validation");

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (needsQr && !status?.connected) {
      fetchQr();
      pollRef.current = setInterval(fetchQr, QR_POLL_MS);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [status?.status, status?.connected, fetchQr]);

  const meta = getStatusMeta(status?.status);
  const isConnected = Boolean(status?.connected) || status?.status === "connected";
  const showQr = Boolean(status?.qr) && !isConnected;
  const staleQr = isStaleQrSession(status);
  const showSessionCleanup = !isConnected && needsSessionCleanup(status);
  const isPaused = status?.status === "paused" || status?.paused;

  return (
    <Card sx={{ p: 3, height: "100%" }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <VuiBox>
          <VuiTypography variant="h5" color="white" fontWeight="bold">
            Conexión WhatsApp
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Vincula tu número receptor para enviar notificaciones automáticas
          </VuiTypography>
        </VuiBox>
        <Chip label={meta.label} color={meta.color} size="small" variant="filled" />
      </VuiBox>

      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={6}>
          <CircularProgress color="info" size={32} />
        </VuiBox>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={showQr ? 6 : 12}>
            <VuiBox
              sx={{
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.08)",
                p: 2.5,
                background: "linear-gradient(127deg, rgba(6,11,38,0.94) 28%, rgba(26,35,126,0.35) 100%)",
              }}
            >
              <VuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1}>
                Estado
              </VuiTypography>
              <VuiTypography variant="h6" color="white" fontWeight="medium" mb={2}>
                {meta.label}
              </VuiTypography>

              {status?.connectedPhone && (
                <VuiTypography variant="button" color="text" display="block" mb={0.5}>
                  Número conectado: {formatDisplayPhone(status.connectedPhone)}
                </VuiTypography>
              )}
              <VuiBox
                mt={2}
                pt={2}
                sx={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
              >
                <VuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1}>
                  Destinatarios de alertas
                </VuiTypography>
                <NotificationRecipientsSummary recipients={displayRecipients} />
              </VuiBox>
              {status?.whatsappName && (
                <VuiTypography variant="button" color="text" display="block" mb={0.5}>
                  Perfil: {status.whatsappName}
                </VuiTypography>
              )}
              {status?.lastError && (
                <VuiTypography variant="caption" color="error" display="block" mt={1}>
                  {status.lastError}
                  {staleQr && !isConnected && (
                    <> — Pulse «Generar nuevo QR» para continuar.</>
                  )}
                </VuiTypography>
              )}

              <VuiBox display="flex" gap={1.5} mt={3} flexWrap="wrap">
                {isConnected ? (
                  <>
                    <VuiButton
                      color="warning"
                      variant="outlined"
                      onClick={handlePause}
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Pausando..." : "Pausar conexión"}
                    </VuiButton>
                    <VuiButton
                      color="error"
                      variant="gradient"
                      onClick={handleLogout}
                      disabled={actionLoading}
                    >
                      Cerrar sesión
                    </VuiButton>
                  </>
                ) : (
                  <VuiButton
                    color="info"
                    variant="gradient"
                    onClick={handleConnect}
                    disabled={isSessionBusy}
                  >
                    {actionLoading
                      ? "Conectando (puede tardar 1-2 min)..."
                      : staleQr
                        ? "Generar nuevo QR"
                        : isPaused
                          ? "Reanudar conexión"
                          : "Conectar WhatsApp"}
                  </VuiButton>
                )}
                {isPaused && status?.authPreserved !== false && (
                  <VuiTypography variant="caption" color="text" display="block" width="100%">
                    Auth conservado — reanudar no requiere QR nuevo
                  </VuiTypography>
                )}
                {showSessionCleanup && (
                  <VuiButton
                    color="error"
                    variant="outlined"
                    onClick={handleResetSession}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Limpiando..." : "Limpiar sesión"}
                  </VuiButton>
                )}
                <WaOutlineButton onClick={fetchStatus} disabled={actionLoading}>
                  Actualizar
                </WaOutlineButton>
              </VuiBox>
            </VuiBox>
          </Grid>

          {showQr && (
            <Grid item xs={12} md={6}>
              <VuiBox
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  p: 2.5,
                  textAlign: "center",
                  background: "#fff",
                }}
              >
                <VuiTypography variant="button" color="dark" fontWeight="bold" display="block" mb={1.5}>
                  Escanea con WhatsApp
                </VuiTypography>
                <VuiBox
                  component="img"
                  src={status.qr}
                  alt="Código QR WhatsApp"
                  sx={{ width: "100%", maxWidth: 280, borderRadius: "12px" }}
                />
                <VuiTypography variant="caption" color="text" display="block" mt={1.5}>
                  WhatsApp → Dispositivos vinculados → Vincular dispositivo
                </VuiTypography>
              </VuiBox>
            </Grid>
          )}
        </Grid>
      )}

      {error && (
        <VuiTypography variant="caption" color="error" display="block" mt={2}>
          {error}
        </VuiTypography>
      )}
    </Card>
  );
}
